import type { FC } from 'react';
import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// studio.table.header.menuItem.booleans.*
import cn from 'classnames';
import {
  BiData,
  BuyCoreButtonBiEvent,
  StudioBatchUpdateBiEvent,
  UnlistItemConfirmBiEvent,
  SellYourArtClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import VerticalEllipsis from '@wix/da-ds/pkg/Icons/16x16/VerticalEllipsis';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import {
  DropdownItem,
  NullableDropdownItem,
} from '@wix/da-ds/pkg/Dropdown/types';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import TextButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/TextButtonWithContextMenu';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import { useUnpublishFlow } from '@wix/da-shared-react/pkg/utils/hooks/useUnpublishFlow';
import { OnUnpublishCompleteAction } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import BatchSellGhostButton from '../../../StudioTableHeader/_partials/BatchSellGhostButton';
import { useBatchSellAction } from '../../../StudioTableHeader/_partials/BatchSellGhostButton/useBatchSellAction';
import AttributeBatchEdit from '../../../batchActions/AttributeBatchEdit';
import BatchEditOrDeleteConfirmation from '../../../../modals/BatchEditOrDeleteConfirmation';
import { BatchAction, StudioSection } from '../../../../types/store';
import MoveToFolderMenu from '../../../stash/MoveToFolderMenu';
import { getIsMergeSupported } from '../../../../selectors/studio';

import s from './SelectedLayout.scss';

export interface Props {
  isUnpublishedDataset?: boolean;
  isRootFolder?: boolean;
  allIsSelected?: boolean;
  selectedCount: number;
  activeSection: StudioSection;
  contentType?: string;
  batchAction?: BatchAction;
  onEditClick: () => void;
  onSelectAll?: (isSelected: boolean) => void;
  onSubmit: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onMoveToStash: () => void;
  onMoveToDrafts: () => void;
  batchMerge: () => void;
  onSelectBatchAction: (action: BatchAction) => void;
  onMoveToFolder: (targetFolderId: number) => void;
  onCreateFolderFromSelection: () => void;
}

const SelectedLayout: FC<Props> = ({
  isUnpublishedDataset,
  allIsSelected,
  selectedCount,
  activeSection,
  contentType,
  batchAction,
  onEditClick,
  onSelectAll,
  onSubmit,
  onUnpublish,
  onDelete,
  batchMerge,
  onMoveToStash,
  onMoveToDrafts,
  onSelectBatchAction,
  onMoveToFolder,
  onCreateFolderFromSelection,
}) => {
  const { t } = useTranslation();
  const labelT = (key: string) =>
    t(`studio.table.header.menuItem.booleans.${key}.label`);

  const isPosts = contentType === 'posts';
  const isPublishedSection = activeSection === StudioSection.PUBLISHED;
  const isEditable = !(activeSection === StudioSection.PUBLISHED && isPosts);
  const isMultiSelect = selectedCount > 1;
  const sellInBatchSections = [
    StudioSection.PUBLISHED,
    StudioSection.DRAFTS,
    StudioSection.PLAYGROUND,
    StudioSection.STASH,
  ];
  const isEnabledSection = sellInBatchSections.includes(activeSection);
  const canSellInBatch = isEnabledSection && !isPosts && isMultiSelect;
  const sellInBatchSupported = canSellInBatch;
  const showBatchSellButton = isMultiSelect && isPublishedSection && !isPosts;

  const { hasCoreMembership } = useContext(SessionContext);
  const canSubmit = hasCoreMembership || selectedCount < 2;

  const [isShowingConfirmation, setShowingConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);

  const initiateUnpublishFlow = useUnpublishFlow({
    onConfirmOverride: onUnpublish,
    onCompleteAction: OnUnpublishCompleteAction.Toast,
    isPlural: selectedCount !== 1,
    confirmBtnBiData: BiData<UnlistItemConfirmBiEvent>({
      evid: 359,
      sectionname: 'batch_actions',
    }),
  });

  const onCancelDelete = () => setShowDeleteConfirmation(false);
  const onDeleteClick = () => setShowDeleteConfirmation(true);
  const onRequestDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
    onDelete();
  }, [onDelete]);

  const labelingDropdownItems: DropdownItem[] = [
    {
      items: [
        {
          label: labelT('ai_add'),
          value: BatchAction.BOOL_AI_ADD,
        },
        {
          label: labelT('ai_del'),
          value: BatchAction.BOOL_AI_DEL,
        },
      ],
    },
    {
      items: [
        {
          label: labelT('noai_add'),
          value: BatchAction.BOOL_NOAI_ADD,
        },
        {
          label: labelT('noai_del'),
          value: BatchAction.BOOL_NOAI_DEL,
        },
      ],
    },
    {
      items: [
        {
          label: labelT('mature_add'),
          value: BatchAction.BOOL_MATURE_ADD,
        },
        {
          label: labelT('mature_del'),
          value: BatchAction.BOOL_MATURE_DEL,
        },
      ],
    },
  ];
  const showAttributeBatchEdit =
    batchAction &&
    [
      BatchAction.BOOL_AI_ADD,
      BatchAction.BOOL_AI_DEL,
      BatchAction.BOOL_MATURE_ADD,
      BatchAction.BOOL_MATURE_DEL,
      BatchAction.BOOL_NOAI_ADD,
      BatchAction.BOOL_NOAI_DEL,
    ].includes(batchAction);

  const labelActionsSupported =
    activeSection !== StudioSection.JOURNALS &&
    activeSection !== StudioSection.APPS &&
    activeSection !== StudioSection.APPS_UNLISTED &&
    !(activeSection === StudioSection.PUBLISHED && isPosts);
  const folderActionsSupported = activeSection === StudioSection.STASH;
  const unpublishSupported = [
    StudioSection.PUBLISHED,
    StudioSection.PRODUCTS_EXCLUSIVE,
    StudioSection.PRODUCTS_PCP,
  ].includes(activeSection);
  const moveToStashSupported = [
    StudioSection.PLAYGROUND,
    StudioSection.DRAFTS,
  ].includes(activeSection);
  const moveToDraftsSupported = activeSection === StudioSection.PLAYGROUND;
  const mergeActionSupported =
    useSelector(getIsMergeSupported) && selectedCount > 1;

  const {
    selectedDeviations: batchSellDeviations,
    selectedIds: batchSellIds,
    handleClick: handleBatchSellClick,
    submenuItems: batchSellSubmenuItems,
  } = useBatchSellAction();

  const sellBiData = sellInBatchSupported
    ? BiData<SellYourArtClickBiEvent>({
        evid: 353,
        deviationid: batchSellIds[0],
        sectionname: BiContextProvidedValue,
        items_count: batchSellDeviations.length,
        deviationid_list: batchSellIds.join(','),
      })
    : null;

  const actionDropdownItems: NullableDropdownItem[] = [
    folderActionsSupported
      ? {
          label: t('studio.table.actions.folders.move'),
          submenu: (
            <MoveToFolderMenu
              onMoveToFolder={targetFolderId => onMoveToFolder(targetFolderId)}
            />
          ),
          estimatedSubmenuWidth: 200,
        }
      : null,

    folderActionsSupported
      ? {
          label: t('studio.table.actions.folders.createFromSelection'),
          onClick: onCreateFolderFromSelection,
        }
      : null,

    unpublishSupported && {
      label: t('studio.table.actions.folders.unpublish'),
      onClick: initiateUnpublishFlow,
      biData: BiData<StudioBatchUpdateBiEvent>({
        evid: 12,
        update_type: 'unlist',
        items_count: selectedCount,
      }),
    },

    moveToStashSupported && {
      label: t('studio.table.header.menuItem.other.moveToStash.label'),
      onClick: onMoveToStash,
      biData: BiData<StudioBatchUpdateBiEvent>({
        evid: 12,
        update_type: 'move_to_stash',
        items_count: selectedCount,
      }),
    },
    moveToDraftsSupported && {
      label: t('studio.table.header.menuItem.other.moveToDrafts.label'),
      onClick: onMoveToDrafts,
      biData: BiData<StudioBatchUpdateBiEvent>({
        evid: 12,
        update_type: 'move_to_drafts',
        items_count: selectedCount,
      }),
    },

    mergeActionSupported
      ? {
          label: t('studio.table.header.mobile.actions.merge'),
          onClick: batchMerge,
          className: s['merge-action'],
        }
      : null,

    sellInBatchSupported && {
      label: t('studio.table.header.sell'),
      onClick: handleBatchSellClick,
      biData: sellBiData,
      submenu: batchSellSubmenuItems ? (
        <StandardMenu
          id="move-to-folder-menu"
          menuClassName={s['folders-menu']}
          items={batchSellSubmenuItems}
        />
      ) : undefined,
    },
    {
      label: t('studio.table.header.mobile.actions.delete'),
      onClick: onDeleteClick,
    },
  ];

  return (
    <>
      <div className={cn(s['root'], isUnpublishedDataset && s['drafts'])}>
        <div className={s['top']}>
          <Checkbox
            className={s['select-checkbox']}
            labelClassName={s['selected-count']}
            isChecked={allIsSelected}
            onChange={onSelectAll}
          >
            {t('studio.table.header.selectedCount', { count: selectedCount })}
          </Checkbox>

          <div className={s['actions']}>
            {labelActionsSupported && (
              <TextButtonWithContextMenu
                className={s['menu-button']}
                popperManagerProps={{ isInPopup: true }}
                standardMenuProps={{ onSelect: onSelectBatchAction }}
                items={labelingDropdownItems}
                size="small"
                variant="main"
                icon={AddIcon}
                iconProps={{
                  size: IconSize.SMALL,
                  disableDefaultColors: true,
                }}
              >
                {t('studio.table.header.mobile.labelingDropdown.buttonText')}
              </TextButtonWithContextMenu>
            )}

            <IconButtonWithContextMenu
              className={cn(s['menu-button'], s['more-button'])}
              popperManagerProps={{ isInPopup: true }}
              placement="bottom-end"
              items={actionDropdownItems}
              size={IconSize.MEDIUM}
              variant="main"
              icon={VerticalEllipsis}
              aria-label={t('siteHeader.more')}
            />
          </div>
        </div>

        {(isEditable || canSellInBatch || isUnpublishedDataset) && (
          <div className={s['bottom']}>
            {isEditable && (
              <div className={s['selection-cta']}>
                <GhostButton
                  size="medium"
                  variant={canSubmit ? 'main' : 'core-orange'}
                  onClick={onEditClick}
                  fillContainerWidth
                >
                  {t('studio.table.header.mobile.edit')}
                </GhostButton>
              </div>
            )}
            {showBatchSellButton && (
              <BatchSellGhostButton className={s['selection-cta']} />
            )}
            {isUnpublishedDataset && canSubmit && (
              <div className={s['selection-cta']}>
                <SolidButton
                  size="medium"
                  variant="approval-green"
                  onClick={() => setShowingConfirmation(true)}
                  fillContainerWidth
                >
                  {t('studio.table.header.mobile.submitSelected')}
                </SolidButton>
              </div>
            )}
            {isUnpublishedDataset && !canSubmit && (
              <div className={s['selection-cta']}>
                <BuyCoreBrandedButton
                  size="medium"
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.STUDIO_MULTI_SUBMIT,
                  })}
                  withMobileDefaultLabel={false}
                  withSaleIndicator={false}
                  fillContainerWidth
                >
                  {t('studio.table.header.mobile.submitNonCore', {
                    selectedCount,
                  })}
                </BuyCoreBrandedButton>
              </div>
            )}
          </div>
        )}
      </div>
      {isUnpublishedDataset && canSubmit && (
        <ConfirmationModal
          isOpen={isShowingConfirmation}
          title={t(`studio.modals.confirm.submit.title`)}
          text={
            <div>
              {t(`studio.modals.confirm.submit.text`, {
                count: selectedCount,
              })}
            </div>
          }
          confirmBtnLabel={t(`studio.modals.confirm.submit.confirmCta`)}
          cancelBtnLabel={t('common.cancel')}
          confirmBtnBiData={BiData<StudioBatchUpdateBiEvent>({
            evid: 12,
            update_type: 'submit',
            items_count: selectedCount,
          })}
          onConfirm={() => {
            setShowingConfirmation(false);
            onSubmit();
          }}
          onCancel={() => setShowingConfirmation(false)}
        />
      )}
      {showDeleteConfirmation && (
        <BatchEditOrDeleteConfirmation
          isOpen
          selectedCount={selectedCount}
          onConfirm={onRequestDelete}
          onCancel={onCancelDelete}
          variant="delete"
        />
      )}
      {showAttributeBatchEdit && (
        <AttributeBatchEdit batchAction={batchAction} />
      )}
    </>
  );
};
SelectedLayout.displayName = 'SelectedLayout';

export default SelectedLayout;
